/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { inject } from '@angular/core';
import { Router, CanActivateFn, CanActivateChildFn, UrlTree, ResolveFn } from '@angular/router';

import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { AccountCreationStore } from '../auth/services/account-creation-store.service';
import { combineLatest, of } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';
import { RallyPointService } from 'src/app/services/rally-point.service';
import { RallyStartComponent } from './rally-start/rally-start.component';
import { UserDataResolver } from 'src/app/services/routing/user-data-resolver.service';
import { AppState } from 'src/app/services/app-state.service';

/**
 * Preloads the rally data if available (the code is present in the state).
 *
 * @returns boolean
 */
export const preloadRallyData: CanActivateFn = () => {
	const logger = LoggerServiceFactory.create('RallyPointResolverService');
	const accountCreationStore = inject(AccountCreationStore);
	const rallyPointService = inject(RallyPointService);

	logger.log('preloadRallyData');
	return combineLatest([accountCreationStore.accessCode$, accountCreationStore.accessCodeData$]).pipe(
		take(1),
		switchMap(([accessCode, accessCodeData]) => {
			if (accessCode && !accessCodeData) {
				const getAccessCode$ = rallyPointService.getAccessCode$(accessCode);
				// Using the nukeObs$ to complete the getAccessCode$ when we nuke the store, see the comments on accountCreationStore for further details
				const getAccessCodeAndClean$ = getAccessCode$.pipe(takeUntil(accountCreationStore.nukeObs$));
				accountCreationStore.setAccessCodeData(getAccessCodeAndClean$);
				return getAccessCode$;
			}
			return of(null);
		}),
		map(() => true)
	);
};

/**
 * Checks if the code is valid and redirect to /start in case it is not.
 * In case of RallyStartComponent (/rally) it returns true.
 *
 * @returns boolean | UrlTree
 */
export const isRallyCodeValid: CanActivateChildFn = (route, state) => {
	const logger = LoggerServiceFactory.create('RallyPointResolverService');
	const accountCreationStore = inject(AccountCreationStore);
	const router = inject(Router);

	logger.log('isRallyCodeValid', route, state);
	if (route.component === RallyStartComponent) {
		return true;
	}
	return accountCreationStore.accessCodeData$.pipe(
		take(1),
		map((accessCodeData) => {
			const isCodeValid = accessCodeData?._isValid;
			if (!isCodeValid) {
				return router.parseUrl('/auth/rally/start');
			}
			return true;
		})
	);
};

/**
 * Checks if the user is authed and test the access code data to know if we can redirect the user to the invited project
 *
 * @returns boolean | UrlTree
 */
export const redirectIfSSOLoginHappened: CanActivateFn = (route, state) => {
	const userDataResolver = inject(UserDataResolver);
	const appState = inject(AppState);
	const accountCreationStore = inject(AccountCreationStore);
	const rallyPointService = inject(RallyPointService);

	return userDataResolver.canActivate(route, state).pipe(
		switchMap(() => combineLatest([appState.waitForUser$, accountCreationStore.accessCodeData$])),
		take(1),
		switchMap(([userData, accessCodeData]) => {
			if (userData) {
				return rallyPointService.testAccessCodeAndGetURLTree$(accessCodeData);
			}
			return of(null as UrlTree);
		}),
		map((result) => {
			if (result) {
				accountCreationStore.nukeState();
				return result;
			}
			return true;
		})
	);
};

export const isRallyRouteResolver: ResolveFn<boolean> = () => {
	return true;
};
